import { UserManager, WebStorageStateStore, type User, type UserManagerSettings } from "oidc-client-ts";

import type { RuntimeAuthConfig } from "../config/runtime";

const userManagers = new Map<string, UserManager>();

function buildMetadata(auth: RuntimeAuthConfig): UserManagerSettings["metadata"] {
  const authority = auth.issuerUrl ?? auth.domain;

  return {
    issuer: authority,
    authorization_endpoint: `${auth.domain}/oauth2/authorize`,
    token_endpoint: `${auth.domain}/oauth2/token`,
    userinfo_endpoint: `${auth.domain}/oauth2/userInfo`,
    jwks_uri: `${authority}/.well-known/jwks.json`,
    end_session_endpoint: `${auth.domain}/logout`,
  };
}

export function getOidcUserManager(auth: RuntimeAuthConfig) {
  const authority = auth.issuerUrl ?? auth.domain;
  const cacheKey = JSON.stringify({
    authority,
    domain: auth.domain,
    client: auth.webClientId,
    redirect: auth.redirectUri,
    logout: auth.logoutUri,
    scopes: auth.scopes,
  });

  const existing = userManagers.get(cacheKey);
  if (existing) {
    return existing;
  }

  const manager = new UserManager({
    authority,
    metadata: buildMetadata(auth),
    client_id: auth.webClientId,
    redirect_uri: auth.redirectUri,
    post_logout_redirect_uri: auth.logoutUri,
    response_type: "code",
    scope: auth.scopes.join(" "),
    automaticSilentRenew: true,
    monitorSession: false,
    redirectMethod: "replace",
    revokeTokensOnSignout: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });

  userManagers.set(cacheKey, manager);
  return manager;
}

export function isAuthenticatedUser(user: User | null) {
  return Boolean(user && !user.expired);
}

export function buildCognitoLogoutUrl(auth: RuntimeAuthConfig) {
  const logoutUrl = new URL("/logout", auth.domain);
  logoutUrl.searchParams.set("client_id", auth.webClientId);
  logoutUrl.searchParams.set("logout_uri", auth.logoutUri);
  return logoutUrl.toString();
}
